/**
 * Canvas component for Babylon.js rendering
 */

import type { IDisposable, Scene } from "@babylonjs/core";
import type React from "react";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useBabylonManager } from "../hooks/useBabylonManager";
import { useEngine } from "../hooks/useEngine";
import { useScene } from "../hooks/useScene";
import type { BabylonCanvasProps } from "../types";

// Debug mode support - dynamic import to avoid bundle bloat
// Uses Inspector v2 API (ShowInspector) which returns IDisposable for cleanup
// Includes custom services:
// - SelectionBridgeService: Scene Explorer auto-reveal on mesh selection
// - StageControlsService: Stage-specific controls panel (rune variants, gem count, etc.)
async function loadBabylonInspector(scene: Scene): Promise<IDisposable | null> {
  try {
    // Dynamic import to avoid increasing bundle size
    const [
      { ShowInspector },
      { SelectionBridgeServiceDefinition },
      { StageControlsServiceDefinition },
    ] = await Promise.all([
      import("@babylonjs/inspector"),
      import("../../inspector/SelectionBridgeService"),
      import("../../inspector/StageControlsService"),
    ]);

    // Show inspector using v2 API with custom services
    const token = ShowInspector(scene, {
      autoResizeEngine: true,
      serviceDefinitions: [
        SelectionBridgeServiceDefinition,
        StageControlsServiceDefinition,
      ],
      themeMode: "system",
    });

    console.log(
      "[Debug Mode] Babylon Inspector v2 loaded with selection bridge and stage controls",
    );
    return token;
  } catch (error) {
    console.warn("[Debug Mode] Failed to load Babylon Inspector:", error);
    return null;
  }
}

/**
 * Canvas component for rendering Babylon.js scenes
 * Uses forwardRef to allow parent components to access the canvas element
 */
export const BabylonCanvas = forwardRef<HTMLCanvasElement, BabylonCanvasProps>(
  (
    {
      sceneId,
      className,
      style,
      width = "100%",
      height = "100%",
      onSceneReady,
      onEngineReady,
      autoRender = true,
      autoResize = true,
      onRender,
      debugMode = false,
      disableCameraControls = false,
      disableCursorHandling = false,
    },
    ref,
  ) => {
    // Internal ref for canvas element
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Hooks for manager, engine, and scene
    const {
      initialize: initializeManager,
      isInitialized: managerInitialized,
      isInitializing: managerInitializing,
      manager,
    } = useBabylonManager();
    const { engine, createEngine, startRenderLoop, stopRenderLoop, resize } =
      useEngine();
    const { createScene } = useScene(sceneId);

    // Initialize state tracking
    const isInitializedRef = useRef(false);
    const [isSceneReady, setIsSceneReady] = useState(false);
    const sceneReadyRef = useRef(false);
    const [isGrabbing, setIsGrabbing] = useState(false);
    const inspectorTokenRef = useRef<IDisposable | null>(null);

    // Ensure the manager begins initialization if it has not started yet
    useEffect(() => {
      if (!initializeManager) {
        return;
      }

      let isActive = true;

      const alreadyInitializing =
        manager?.isInitialized === true ||
        manager?.isInitializing === true ||
        managerInitialized ||
        managerInitializing;

      if (!alreadyInitializing) {
        initializeManager().catch((error) => {
          if (!isActive) {
            return;
          }
          console.error(
            "Failed to initialize BabylonManager from BabylonCanvas:",
            error,
          );
        });
      }

      return () => {
        isActive = false;
      };
    }, [initializeManager, manager, managerInitialized, managerInitializing]);

    // Combine refs (external ref and internal ref)
    const setRefs = useCallback(
      (element: HTMLCanvasElement | null) => {
        canvasRef.current = element;
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    // Create default camera fallback
    const createDefaultCamera = useCallback(
      async (scene: Scene) => {
        const { ArcRotateCamera, Vector3 } = await import("@babylonjs/core");

        const camera = new ArcRotateCamera(
          "defaultCamera",
          -Math.PI / 2,
          Math.PI / 2.5,
          10,
          Vector3.Zero(),
          scene,
        );

        // Only attach controls if not disabled
        if (canvasRef.current && !disableCameraControls) {
          camera.attachControl(canvasRef.current);
        }

        // Note: Fallback camera is created as a safety net before onSceneReady runs.
        // StageManager.setupCamera() will replace it with the proper stage camera.
        return camera;
      },
      [disableCameraControls],
    );

    // Initialize engine and scene
    useEffect(() => {
      const initializeAsync = async () => {
        if (!canvasRef.current || isInitializedRef.current) {
          return;
        }

        // Only run in browser environment
        if (typeof window === "undefined") {
          return;
        }

        try {
          isInitializedRef.current = true;

          // Create engine with canvas
          const newEngine = await createEngine(canvasRef.current);
          onEngineReady?.(newEngine);

          // Create scene
          const newScene = await createScene();

          // Create default camera immediately after scene creation
          await createDefaultCamera(newScene);

          // Wrap onSceneReady to signal when scene is ready
          const wrappedOnSceneReady = async (scene: Scene) => {
            try {
              // Call user's scene setup
              await onSceneReady?.(scene);

              // Load Babylon Inspector if debug mode is enabled
              if (debugMode) {
                inspectorTokenRef.current = await loadBabylonInspector(scene);
              }

              // Signal that scene is ready
              sceneReadyRef.current = true;
              setIsSceneReady(true);
            } catch (error) {
              console.error("Scene setup failed:", error);
              // Still mark as ready to allow fallback camera to work
              sceneReadyRef.current = true;
              setIsSceneReady(true);
            }
          };

          // Call the wrapped scene ready callback
          await wrappedOnSceneReady(newScene);
        } catch (error) {
          console.error("Failed to initialize Babylon canvas:", error);
          isInitializedRef.current = false;
        }
      };

      initializeAsync();
    }, [
      createEngine,
      createScene,
      createDefaultCamera,
      onSceneReady,
      onEngineReady,
    ]);

    // Get scene from hook at component level
    const { scene } = useScene(sceneId);

    // Start render loop only after scene is ready
    useEffect(() => {
      if (!isSceneReady || !autoRender || !scene) {
        return;
      }

      startRenderLoop(() => {
        try {
          if (scene && !scene.isDisposed) {
            onRender?.(scene);
            scene.render();
          }
        } catch (error) {
          // Silently ignore WebGL disposal errors that happen during test cleanup
          // These occur when the WebGL context is destroyed while rendering
          if (
            error instanceof Error &&
            (error.message.includes("program") ||
              error.message.includes("WebGL") ||
              error.message.includes("disposed"))
          ) {
            // Expected during disposal, don't log
            return;
          }
          // Log other unexpected errors
          console.error("Render error:", error);
        }
      });
    }, [isSceneReady, autoRender, scene, startRenderLoop, onRender]);

    // Handle resize - both window resize and container resize via ResizeObserver
    useEffect(() => {
      if (!autoResize || !engine) {
        return;
      }

      const handleResize = () => {
        resize();
      };

      window.addEventListener("resize", handleResize);

      // ResizeObserver detects container size changes (e.g., sidebar collapse)
      // that don't trigger window resize events
      const resizeObserver = new ResizeObserver(handleResize);
      if (canvasRef.current?.parentElement) {
        resizeObserver.observe(canvasRef.current.parentElement);
      }

      return () => {
        window.removeEventListener("resize", handleResize);
        resizeObserver.disconnect();
      };
    }, [autoResize, engine, resize]);

    // Cleanup render loop on unmount
    useEffect(() => {
      return () => {
        // Stop render loop when component unmounts
        if (stopRenderLoop) {
          stopRenderLoop();
        }
      };
    }, [stopRenderLoop]);

    // Cleanup inspector on unmount
    useEffect(() => {
      return () => {
        // Dispose inspector token when component unmounts
        if (inspectorTokenRef.current) {
          inspectorTokenRef.current.dispose();
          inspectorTokenRef.current = null;
        }
      };
    }, []); // Empty deps: runs only on unmount, ref doesn't need to be a dependency

    // Handle debugMode toggle after initialization
    useEffect(() => {
      // Skip if scene not ready (initialization handles initial debugMode)
      if (!isSceneReady || !scene) {
        return;
      }

      const handleDebugToggle = async () => {
        if (debugMode) {
          // Load inspector if not already loaded
          if (!inspectorTokenRef.current) {
            inspectorTokenRef.current = await loadBabylonInspector(scene);
          }
        } else {
          // Dispose inspector if loaded
          if (inspectorTokenRef.current) {
            inspectorTokenRef.current.dispose();
            inspectorTokenRef.current = null;
            console.log("[Debug Mode] Babylon Inspector closed");
          }
        }
      };

      handleDebugToggle();
    }, [debugMode, isSceneReady, scene]);

    // Handle pointer events for cursor changes
    const handlePointerDown = useCallback(() => {
      if (!disableCursorHandling && !disableCameraControls) {
        setIsGrabbing(true);
      }
    }, [disableCursorHandling, disableCameraControls]);

    const handlePointerUp = useCallback(() => {
      if (!disableCursorHandling) {
        setIsGrabbing(false);
      }
    }, [disableCursorHandling]);

    const handlePointerLeave = useCallback(() => {
      if (!disableCursorHandling) {
        setIsGrabbing(false);
      }
    }, [disableCursorHandling]);

    // Build cursor classes based on state
    const cursorClasses =
      !disableCursorHandling && !disableCameraControls
        ? isGrabbing
          ? "cursor-grabbing"
          : "cursor-grab"
        : "";

    // Canvas styles
    const canvasStyle: React.CSSProperties = {
      display: "block",
      height,
      width,
      ...style,
    };

    // Combine className with cursor classes
    const combinedClassName = [className, cursorClasses]
      .filter(Boolean)
      .join(" ");

    return (
      <canvas
        className={combinedClassName}
        onPointerDown={handlePointerDown}
        onPointerLeave={handlePointerLeave}
        onPointerUp={handlePointerUp}
        ref={setRefs}
        style={canvasStyle}
        tabIndex={0}
      />
    );
  },
);

/**
 * Display name for debugging
 */
BabylonCanvas.displayName = "BabylonCanvas";
